import React, { useState, useRef } from 'react';
import ApiPost from '../../../../../../api-controller-mod/api-serv/post-api/postApi';
import { HeaderData, HeaderCta, NavItem, MediaSaveCallback } from './types';
import { LabelRow, SplitRow, DraftTextField } from './primitives';
import ImageUploadField from './imageUploadField';

interface HeaderEditorProps {
  data:               HeaderData;
  onChange:           (v: HeaderData) => void;
  onMediaClick?:      (onSave: MediaSaveCallback) => void;
  userId:             string;
  registerMediaUsage: (mediaUuid: string) => void;
}

const HeaderEditor: React.FC<HeaderEditorProps> = ({
  data, onChange, onMediaClick, userId, registerMediaUsage,
}) => {
  const orig = useRef(JSON.parse(JSON.stringify(data)));

  // navToggles is local state — nav items are array-indexed so we need
  // a local array to track each one; updates call onChange to sync parent
  const [navToggles, setNavToggles] = useState<boolean[]>(
    () => data.navigation.items.map(i => i.visible)
  );

  // ── nav helpers ────────────────────────────────────────────────────────────
  const setNavItem = (idx: number, p: Partial<NavItem>) => {
    const items = data.navigation.items.map((item, i) => i === idx ? { ...item, ...p } : item);
    onChange({ ...data, navigation: { ...data.navigation, items } });
  };

  const setNavToggle = (idx: number, v: boolean) => {
    setNavToggles(prev => prev.map((t, i) => i === idx ? v : t));
    setNavItem(idx, { visible: v });
  };

  // ── cta helpers ────────────────────────────────────────────────────────────
  const setCta      = (p: Partial<HeaderCta>) => onChange({ ...data, cta: { ...data.cta, ...p } });
  const setCtaLabel = (label: string)          => setCta({ content: { ...data.cta.content, label } });

  return (
    <>
      {/* ── Navigation items ─────────────────────────────────────────────── */}
      {orig.current.navigation.items.map((origItem: NavItem, idx: number) => (
        <React.Fragment key={origItem.key}>
          <LabelRow
            label="Navigation item"
            toggle={{
              checked:  navToggles[idx] ?? origItem.visible,
              onChange: v => setNavToggle(idx, v),
            }}
          />
          <SplitRow currentText={origItem.label}>
            <DraftTextField placeholder="Navigation name" onChange={v => setNavItem(idx, { label: v })} />
          </SplitRow>
        </React.Fragment>
      ))}

      {/* ── Primary button — fully controlled from data prop ─────────────── */}
      <LabelRow
        label="Primary button"
        toggle={{
          checked:  data.cta.visible,
          onChange: v => setCta({ visible: v }),
        }}
      />
      <SplitRow currentText={orig.current.cta.content.label}>
        <DraftTextField placeholder="Text button" onChange={v => setCtaLabel(v)} />
      </SplitRow>

      {/* ── Company logo — fully controlled from data prop ───────────────── */}
      <LabelRow
        label="Company logo"
        toggle={{
          checked:  data.logo.image.visible,
          onChange: (v: boolean) => {
            onChange({ ...data, logo: { ...data.logo, image: { ...data.logo.image, visible: v } } });
          },
        }}
      />
      <ImageUploadField
        label=""
        currentSrc={data.logo.image.src || undefined}
        defaultName=" LOGO"
        onFile={async file => {
          try {
            const formData = new FormData();
            formData.append('file',        file);
            formData.append('type',        'IMAGE');
            formData.append('filename',    file.name);
            formData.append('alt',         '');
            formData.append('description', '');

            const res          = await ApiPost(`/api/v1/media/upload?userUuid=${userId}`, formData);
            const uploadedUrl  = (res as any)?.data?.data?.url  as string | undefined;
            const uploadedUuid = (res as any)?.data?.data?.uuid as string | undefined;

            if (uploadedUrl) {
              onChange({ ...data, logo: { ...data.logo, image: { ...data.logo.image, src: uploadedUrl } } });
              if (uploadedUuid) registerMediaUsage(uploadedUuid);
              return uploadedUrl;
            }
          } catch (err) {
            console.error('[HeaderLogoUpload] upload failed:', err);
          }
          return undefined;
        }}
        onMediaClick={
          onMediaClick
            ? () => onMediaClick(item => {
                if (item) onChange({ ...data, logo: { ...data.logo, image: { ...data.logo.image, src: item.src } } });
              })
            : undefined
        }
      />
    </>
  );
};

export default HeaderEditor;